import type { IMediaAccepted, IMediaPolicy, IMediaRejection } from './media-policy';

/**
 * Client-side mirror of the server's MMS media limits (see the `MMS` profile in
 * `backend/src/modules/media-library/constants/media-library.constants.ts`).
 * Duplicated deliberately so the composer can reject a file instantly instead
 * of after a round-trip; the server re-validates everything by sniffing bytes.
 *
 * Scoped to MMS only. The email builder's image library is a different profile
 * with its own allow-list and 5 MB cap — see `hooks/use-media-upload.ts`.
 */

/**
 * Carriers — not the providers — are the binding constraint. Telnyx's API
 * rejects above 1 MB and Twilio allows 5 MB, but AT&T caps toll-free MMS at
 * 600 KB, and anything larger is accepted by the API and then silently dropped
 * en route. Refusing at upload is far better than a send that vanishes.
 *
 * It also makes video and audio a tight fit: 600 KB is roughly 3-8 seconds of
 * low-bitrate video, or 30 seconds of 128 kbps MP3.
 */
export const MMS_MAX_MEDIA_BYTES = 600 * 1024;
export const MMS_MAX_MEDIA_KB = Math.round(MMS_MAX_MEDIA_BYTES / 1024);

/** Both providers cap media per message at 10. */
export const MAX_MEDIA_PER_MESSAGE = 10;

/**
 * Extension → canonical content type. Keyed by EXTENSION rather than read from
 * the browser's `File.type`, which cannot be trusted for half of these:
 *
 *   - `.amr`, `.3gp`, `.vcf` and `.ics` often arrive with an empty `type`.
 *   - Chrome reports `.wav` as `audio/wav`, but Twilio's accepted-type list
 *     names only `audio/vnd.wave` and it rejects the send on anything else.
 *
 * The value sent at presign also signs the S3 PUT and becomes the `Content-Type`
 * we later serve, so it has to be exactly the string the server expects.
 */
export const MEDIA_EXTENSION_TO_MIME: Readonly<Record<string, string>> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    bmp: 'image/bmp',
    mp4: 'video/mp4',
    '3gp': 'video/3gpp',
    mov: 'video/quicktime',
    mp3: 'audio/mpeg',
    wav: 'audio/vnd.wave',
    amr: 'audio/amr',
    pdf: 'application/pdf',
    vcf: 'text/x-vcard',
    ics: 'text/calendar',
};

/**
 * Formats users reach for that at least one provider will not carry, mapped to
 * one that works. WebP is refused by Twilio and MPEG by Telnyx; AVI and AAC by
 * both. Rather than make an upload's validity depend on which provider the org
 * happens to have configured, all four are refused with a way forward.
 */
export const UNSUPPORTED_MEDIA_HINTS: Readonly<Record<string, string>> = {
    webp: 'PNG or JPEG',
    svg: 'PNG or JPEG',
    heic: 'PNG or JPEG',
    avi: 'MP4',
    mpeg: 'MP4',
    mpg: 'MP4',
    mkv: 'MP4',
    webm: 'MP4',
    aac: 'MP3 or AMR',
    m4a: 'MP3 or AMR',
    ogg: 'MP3 or AMR',
};

/** Extensions whose 600 KB refusal is worth explaining — a still image fits, a clip usually will not. */
const TIME_BASED_EXTENSIONS: ReadonlySet<string> = new Set([
    'mp4',
    '3gp',
    'mov',
    'mp3',
    'wav',
    'amr',
]);

/** Lowercased extension of a filename, or '' when it has none. */
export function mediaExtensionOf(filename: string): string {
    const dot = filename.lastIndexOf('.');
    return dot > 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

/** Canonical content type for a picked file, or null when it is not supported. */
export function canonicalMediaType(file: File): string | null {
    return MEDIA_EXTENSION_TO_MIME[mediaExtensionOf(file.name)] ?? null;
}

/** Suggested replacement for a file we refuse, or null when we have none to offer. */
export function unsupportedMediaHint(file: File): string | null {
    return UNSUPPORTED_MEDIA_HINTS[mediaExtensionOf(file.name)] ?? null;
}

/** Whether an over-size refusal should suggest shortening rather than re-exporting. */
export function isTimeBasedMedia(file: File): boolean {
    return TIME_BASED_EXTENSIONS.has(mediaExtensionOf(file.name));
}

/**
 * `accept` attribute for the file input. Lists both MIME types AND dotted
 * extensions: browsers will not match `.amr`, `.3gp`, `.vcf` or `.ics` on MIME
 * alone because they assign those no type. Same both-forms shape as
 * `NOTE_ATTACHMENT_ACCEPT` in `features/inbox/constants/note-attachments.ts`.
 */
export const MEDIA_ACCEPT = [
    ...new Set(Object.values(MEDIA_EXTENSION_TO_MIME)),
    ...Object.keys(MEDIA_EXTENSION_TO_MIME).map((ext) => `.${ext}`),
].join(',');

/**
 * The MMS rule set, as an {@link IMediaPolicy} — the default for every
 * composer. Extracted from `useMediaAttachments`'s former inline validation so
 * WhatsApp can supply its own without the hook growing a channel branch.
 */
export const MMS_MEDIA_POLICY: IMediaPolicy = {
    profile: 'MMS',
    maxPerMessage: MAX_MEDIA_PER_MESSAGE,
    accept: MEDIA_ACCEPT,
    inspect(file: File): IMediaAccepted | IMediaRejection {
        const contentType = canonicalMediaType(file);
        if (!contentType) {
            const alternative = unsupportedMediaHint(file);
            return alternative
                ? {
                      key: 'media.errors.badTypeWithHint',
                      defaultValue: '"{{name}}" can\'t be sent over MMS — use {{alt}} instead.',
                      params: { name: file.name, alt: alternative },
                  }
                : {
                      key: 'media.errors.badType',
                      defaultValue: '"{{name}}" is not a supported file type.',
                      params: { name: file.name },
                  };
        }
        if (file.size > MMS_MAX_MEDIA_BYTES) {
            return isTimeBasedMedia(file)
                ? {
                      key: 'media.errors.tooLargeClip',
                      defaultValue:
                          '"{{name}}" is larger than {{max}} KB — try a shorter clip or a lower quality export.',
                      params: { name: file.name, max: MMS_MAX_MEDIA_KB },
                  }
                : {
                      key: 'media.errors.tooLarge',
                      defaultValue:
                          '"{{name}}" is larger than {{max}} KB — carriers drop MMS attachments above that size.',
                      params: { name: file.name, max: MMS_MAX_MEDIA_KB },
                  };
        }
        return { contentType };
    },
};
